import { useMemo } from 'react';

import { IKnmErknmFormSubject } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormSubject';

type IErknmSubjectBaseFieldPaths = {
  [K in keyof IKnmErknmFormSubject]-?: string;
};

interface IErknmSubjectFieldPathsExtended {
  location_manualAddress: string;
  location_regionName: string;
  work_notInFIAS: string;
  work_manualAddress: string;
  work_region: string;
  work_regionName: string;
  factual_notInFIAS: string;
  factual_manualAddress: string;
  factual_region: string;
  factual_regionName: string;
  organizationAutocomplete: string;
  registrationDate: string;
  organizationStatus: string;
}

export interface IErknmSubjectFieldPaths extends IErknmSubjectBaseFieldPaths, IErknmSubjectFieldPathsExtended {}

type IBaseKeys = keyof IErknmSubjectBaseFieldPaths;
type IExtendedKeys = keyof IErknmSubjectFieldPathsExtended;

type IDuplicateKeys = IBaseKeys & IExtendedKeys;

// проверка на дублирование полей из IBaseKeys в IExtendedKeys, дублирующиеся поля будут в IDuplicateKeys
const isAssertNoDuplicates: IDuplicateKeys extends never ? true : false = true;

// eslint-disable-next-line no-unused-expressions, @typescript-eslint/no-unused-expressions
isAssertNoDuplicates;

// объект для генерации списка полей в рантайме
const FIELD_KEYS: Record<keyof IErknmSubjectFieldPaths, true> = {
  // Base fields (from IKnmErknmFormSubject)
  inn: true,
  position: true,
  ogrn: true,
  okveds: true,
  addressFns: true,
  organizationName: true,
  firstOrganizationName: true,
  mspCode: true,
  mspRegistryDate: true,
  checkSubjectResult: true,
  dateCreate: true,
  isAutocomplete: true,
  isFiz: true,
  guid: true,
  isDocumentOrganization: true,
  listId: true,
  isNew: true,
  isEmpty: true,
  nkoForeignAgentsRegistryNumber: true,
  nkoForeignAgentsRegistryInclusionDate: true,
  nkoForeignAgentsRegistryExclusionDate: true,
  isNko: true,
  subjectType: true,
  kpp: true,
  smevRequestStatus: true,
  rafpNza: true,
  isForeignSubjectAgencyNotRegistredInRf: true,
  country: true,
  isSonko: true,
  locationAddress: true,
  workAddress: true,
  factualAddress: true,
  snils: true,
  document: true,
  epguNotifications: true,
  ervkId: true,
  isPresentInItRegistry: true,
  itAccreditationResponse: true,
  smevItAccreditationRequestStatus: true,
  inspectionResults: true,
  isFilledFromLink: true,
  locationregion: true,
  locationnotInFIAS: true,
  locationmanualAddress: true,
  locationfiasRegion: true,
  locationisErrorFiasValue: true,
  locationRegionCodeFromFns: true,

  // Extended fields
  location_manualAddress: true,
  location_regionName: true,
  work_notInFIAS: true,
  work_manualAddress: true,
  work_region: true,
  work_regionName: true,
  factual_notInFIAS: true,
  factual_manualAddress: true,
  factual_region: true,
  factual_regionName: true,
  organizationAutocomplete: true,
  registrationDate: true,
  organizationStatus: true,
};

interface IUseErknmSubjectFieldsProps {
  name: string | undefined;
}

/**
 * хук, который генерирует согласованный набор путей к полям формы.
 *
 * @example
 * const subjectFieldNames = useErknmSubjectFields({ name: 'legalEntity' });
 * <SelectInputField id={subjectFieldNames.subjectType} name={subjectFieldNames.subjectType}/>
 *
 * @param {IUseErknmSubjectFieldsProps} props .
 * @returns {ErknmSubjectFieldPaths} Объект, сопоставляющий логические имена полей с их полными путями в форме.
 */
export const useErknmSubjectFields = ({ name }: IUseErknmSubjectFieldsProps): IErknmSubjectFieldPaths => {
  return useMemo(() => {
    const prefix = name ?? '';

    const rawFields = {} as IErknmSubjectFieldPaths;

    (Object.keys(FIELD_KEYS) as Array<keyof IErknmSubjectFieldPaths>).forEach(key => {
      rawFields[key] = `${prefix}.${key}`;
    });

    return rawFields;
  }, [name]);
};
